// ═══════════════════════════════════════════════════════════════
// frontend/src/pages/PartnerForm.tsx — Create/edit partner
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PartnerApi } from "../lib/api";

export default function PartnerForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: partner } = useQuery({
    queryKey: ["partner", id],
    queryFn: () => PartnerApi.get(Number(id)),
    enabled: isEdit,
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", isCompany: false, type: "contact" });

  useEffect(() => {
    if (partner) setForm({ name: partner.name ?? "", email: partner.email ?? "", phone: partner.phone ?? "", isCompany: partner.is_company ?? false, type: partner.type ?? "contact" });
  }, [partner]);

  const mutation = useMutation({
    mutationFn: (data: any) => isEdit ? PartnerApi.update(Number(id), data) : PartnerApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["partners"] }); navigate("/partners"); },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(form);
  }

  return (
    <div className="max-w-lg mx-auto">
      <h1 className="text-2xl font-bold mb-6">{isEdit ? "Edit Partner" : "New Partner"}</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="isCompany" checked={form.isCompany}
            onChange={(e) => setForm({ ...form, isCompany: e.target.checked, type: e.target.checked ? "company" : "contact" })}
            className="rounded" />
          <label htmlFor="isCompany" className="text-sm font-medium text-gray-700">This is a company</label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={mutation.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
          <button type="button" onClick={() => navigate("/partners")}
            className="px-6 py-2 border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
